import React, { useEffect, useReducer, useState } from "react";
import { useNavigate } from "react-router-dom";
import { capitalizeString, filtersReducer, formatDate, getOrgURLParam, isTrialSubscription } from "../../utility-functions";
import { JService } from "../api-service/ApiService";
import GHeader from "../design/components/GHeader";
import GTable from "../design/components/GTable";
import GModal from "../design/components/GModal";
import Button from "../design/Button";
import { threeDotsIcon} from "../../utility-functions/assetLoader";
import styles from "./VirtualNumber.module.css";
import {useSelector } from "react-redux";
import { enqueueSnackbar } from "notistack";
import Tag from "../whatsapp-business/Components/Tag";
import { CustomTooltip, InsufficientCreditsModal, PaymentMethodModal } from "../design/components/customComponents";
import RemoveVirtualNumberModal from "./RemoveVirtualNumberModal";
import AddVirtualNumberModal from "./AddVirtualNumberModal";

const initialState = {
  locations: {
    all: [],
    selected: [],
  },
  status:{
    all:[
      {id:'Active', name:'Active'},
      {id:'In Progress', name:'In Progress'},
      {id:'Verification Pending', name:'Verification Pending'},
      {id:'Removal Scheduled', name:'Removal Scheduled'},
    ],
    selected: [],
  },
  numberTypes: {
    all: [
      { id: "Calling", name: "Calling" },
      { id: "Voice Broadcast", name: "Voice broadcast" },
    ],
    selected: [],
  },
  searchText: "",
  searchFields: [],
};

const API_BASE_URL = process.env.REACT_APP_BETA;
function VirtualNumber({ handleToggleWidget, isWidgetOpen }) {

  const navigate = useNavigate();
  const reduxAccount = useSelector((state) => state?.account?.value);
  const reduxSubscription = useSelector(
    (state) => state?.account?.value?.subscription,
  );
  const reduxCredits = useSelector((state) => state?.account?.value?.credits);
  const {canManageBillingSubscription} = useSelector((state) => state?.auth?.permissions);
  const locations = useSelector((state) => state?.locations?.value);


  //States
  const [showLoader, setShowLoader] = useState(false);
  const [rows, setRows] = useState([]);
  const [masterRows, setMasterRows] = useState([]);

  // Three dots options
  const [rowBeingEdited, setRowBeingEdited] = useState(null);
  const [rowOptionsCords, setRowOptionsCords] = useState([0, 0]);
  const [showModal, setShowModal] = useState({
    edit: false,
    remove: false,
    add: false,
    payment: false,
    insufficientCredits: false
  })
  const [editRowName, setEditRowName] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const [csvData, setCSVData] = useState({
    headers: [
      ["Name"],
      ["Number"],
      ["Location"],
      ["Status"],
    ],
    data: [],
    ids: [],
  });

  const [availableData, dispatchAvailableData] = useReducer(
    filtersReducer,
    initialState,
  );

  const [vnPayload, setVnPayload] = useState({});

  //Handlers
  const fetchData = () => {
    setShowLoader(true);
    JService.get(API_BASE_URL + `/api/v1/subscriptions/retrieve-vn-addons/${getOrgURLParam()}`)
      .then((res) => {
        if (!res?.success)
          throw new Error(
            res?.message || "Failed to retrieve virtual number addons",
          );
        setRows(res.data);
        setMasterRows(res.data);
      })
      .catch((err) => enqueueSnackbar(err?.message, { variant: "error" }))
      .finally(() => setShowLoader(false));
  };

  const handleScrollWhenRowOptionsOpen = (e) => {
    setRowBeingEdited(null);

    window.removeEventListener("scroll", handleScrollWhenRowOptionsOpen);
  };

  const onSearch = (searchText) => {
    const searchTextLower = searchText?.toLowerCase();
    const temp = masterRows?.filter((row) =>
      row.name?.toLowerCase().includes(searchTextLower) || (row.country_code + " " +  row.number)?.includes(searchTextLower)
    );
    combineSearchAndFilters(temp);
  };

  const combineSearchAndFilters = (arg1) => {
    // if filters and search is empty, set rows if rows != masterRows
    if (
      !availableData?.locations?.selected?.length &&
      !availableData?.numberTypes?.selected?.length &&
      !availableData?.status?.selected?.length &&
      availableData?.searchText.trim()?.length === 0
    ) {
      if (arg1 !== rows) {
        setRows(arg1); //eslint-disable-line
      }
      return;
    }

    // locations selected changed
    if (availableData?.locations?.selected?.length) {
      arg1 = arg1?.filter((te) =>
        availableData?.locations?.selected?.includes(te?.location),
      );
    }

    if(availableData?.status?.selected?.length){
      arg1 = arg1?.filter((te) => 
        availableData?.status?.selected?.includes(te?.status),
      )
    }

    // callTypes selected changes
    if (availableData?.numberTypes?.selected?.length) {
      arg1 = arg1?.filter((te) =>
        availableData?.numberTypes?.selected?.includes(te?.type),
      );
    }

    setRows(arg1);
  };

  const updateName = () => {
    setIsLoading(true);
    const url = "/api/v1/subscriptions/addon-licenses/";

    JService.patch(url, {
      addon: "VIRTUAL NUMBER",
      number_id: rowBeingEdited?.id,
      vn_name: editRowName,
    })
      .then((response) => {
        const res = response.data;

        if (!response?.success)
          throw new Error(
            response?.message || "Error while updating virtual number"
          );
        const updatedRows = masterRows.map((row) => {
          if (row.id === rowBeingEdited.id) {
            return { ...row, name: res?.name };
          }
          return row;
        });
        setRows(updatedRows);
        setMasterRows(updatedRows);
        enqueueSnackbar(response?.message, { variant: "success" });

        setShowModal((prev) => {
          return { ...prev, edit: false };
        });
        setRowBeingEdited(null);
      })
      .catch((err) => enqueueSnackbar(err?.message, { variant: "error" }))
      .finally(() => setIsLoading(false));
  };
  const handleOpenChatWidget = () => {
    if (!isWidgetOpen) {
      handleToggleWidget();
    }
  };

  const handleAddVN = () => {
    setShowModal((prev) => {
      return { ...prev, add: true };
    });
  };

  const purchaseHandler = (data, payableLicenses) => {
    setVnPayload(data);
    if (reduxCredits?.subscription_payments) {
      updateAddonHandler("null", "add", data);
      return;
    } 
    else if(payableLicenses === 0){
      updateAddonHandler(true, "add", data);
      return;
    }
    setShowModal((prev) => {
      return { ...prev, payment: true };
    });
  };

  const updateAddonHandler = (payWithCredits, type, data) => {
    let payload = {
      action: type,
      addon: "VIRTUAL NUMBER",
      ...data,
    };

    if (type === "add" && payWithCredits !== "null") {
      payload = {
        ...payload,
        pay_with_credits: payWithCredits,
      };
    }
    const url = "/api/v1/subscriptions/addon-licenses/";
    setIsLoading(true);
    JService.post(url, JSON.stringify(payload))
      .then((response) => {
        if (type === "add" && response?.url) {
          window.location.href = response?.url;
          return;
        }
        if (response?.message === "Insufficient balance" && type === "add") {
          setShowModal((prev) => {
            return { ...prev, insufficientCredits: true };
          });
          return;
        }
        if (!response?.success) {
          throw new Error(response?.message);
        }
        let msg;
        if (type === "add") {
          msg =
            "Purchase successful. Our support team will contact you shortly to assist with verification.";
        } else if (type === "remove") {
          msg = "Number scheduled for removal.";
        }
        enqueueSnackbar(response?.message || msg, { variant: "success" });
        fetchData();
      })
      .catch((error) => {
        if (
          error?.response?.data?.message === "Insufficient balance" &&
          type === "add"
        ) {
          setShowModal((prev) => {
            return { ...prev, insufficientCredits: true };
          });
          return;
        }
        enqueueSnackbar(error?.message || "Could not update numbers", {
          variant: "error",
        });
      })
      .finally(() => {
        setIsLoading(false);
        setShowModal((prev) => {
          return {
            ...prev,
            remove: false,
            payment: false,
            insufficientCredits: false,
          };
        });
      });
  };

  //Effects

  useEffect(() => {
    if (!locations) return;
    
    const formattedLocations = locations.map((loc) => {
      return {
        id: loc.country,
        name: loc.country,
      };
    });
   
    dispatchAvailableData({
      type: "setPayloadToAllOfType",
      payload: {
        payloadFor: "locations",
        payload: formattedLocations,
      },
    });

  }, [locations]);

  useEffect(() => {
    if (rowBeingEdited) {
      window.addEventListener("scroll", handleScrollWhenRowOptionsOpen);
    }

    return () => {
      window.removeEventListener("scroll", handleScrollWhenRowOptionsOpen);
    };
  }, [rowBeingEdited]);

  useEffect(() => {
    fetchData();
  }, [reduxAccount?.selectedOrg]);

  // UseEffect for Search
  useEffect(() => {
    onSearch(availableData?.searchText);
  }, [availableData, masterRows]);

  // UseEffect to set CSV data
  useEffect(() => {
    setCSVData((prevState) => {
      let newIds = [];
      let newData = [];

      for (let i = 0; i < rows.length; i++) {
        let item = rows[i];
        if (!newIds?.includes(item?.id)) {
          newIds.push(item?.id);
          const name = item.name
            ? capitalizeString(item.name)
            : item.location + "-" + item.id;
          let temp = [
            name,
            (item.number)?.trim()?.length ? item.country_code + " " + item.number : "-",
            item.location,
            item.status,
          ];
          newData.push(temp);
        }
      }
      return { ...prevState, data: newData, ids: newIds };
    });
  }, [rows]);

  //Virtual number columns
  const cols = [
    {
      field: "name",
      headerName: "Name",
      headerClassName: "user-table-header-user-name",
      flex: 1,
      renderCell: (params) => {
        const name = !!params.row?.name
          ? capitalizeString(params.row.name)
          : params?.row?.location + "-" + params?.row?.id;
        return (
          <p
            title={name}
            className="ic-green-500 medium-font b2"
            style={{
              marginLeft: "14px",
              maxWidth: "300px",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {name}
          </p>
        );
      },
    },
    {
      field: "number",
      headerName: "Number",
      flex: 1,
      renderCell: (params) => {
        const cc = params?.row?.country_code ?? '', number = params?.row?.number ?? '';
        const phoneNumber = cc + " " + number
        return (
          <p
            className="t6 regular-font nc-gray-900"
            style={{ fontSize: "14px" }}
          >
            {!phoneNumber?.trim() ? '-' : phoneNumber}
          </p>
        );
      },
    },
    {
      field: "location",
      headerName: "Location",
      flex: 1,
      renderCell: (params) => {
        return (
          <p
            className="t6 regular-font nc-gray-900"
            style={{ fontSize: "14px" }}
          >
            {params?.row?.location}
          </p>
        );
      },
    },
    {
      field: "status",
      headerName: "Status",
      flex: 1,
      renderCell: (params) => {
        const status = params?.row?.status;
        const color =
          status === "Active"
            ? "GREEN"
            : status === "In Progress"
              ? "BLUE"
              : status === "Verification Pending"
                ? "YELLOW"
                : "RED";
        return status === "Active" ? (
          <Tag name={status} quality={color} />
        ) : (
          <CustomTooltip
            placement="top"
            tooltipTitle={
              <div className={styles.vnStatusTooltip}>
                <p>
                  {status === "In Progress"
                    ? "Your number will be allocated soon!"
                    : status === "Verification Pending"
                      ? "Document verification Pending."
                      : `Number scheduled for removal on ${formatDate(reduxSubscription?.end_date)}`}
                </p>
                {status === "In Progress" ||
                status === "Verification Pending" ? (
                  <span
                    onClick={handleOpenChatWidget}
                    className={styles.contactSupport}
                  >
                    Contact support
                  </span>
                ) : null}
              </div>
            }
          >
            <div>
              <Tag name={status} quality={color} />
            </div>
          </CustomTooltip>
        );
      },
    },
    {
      field: "user_count",
      headerName: "Users",
      flex: 0.5
    },
    {
      field: "numberEdit",
      headerName: "",
      flex: 0.5,
      cellClassName: "table-cell-edit", // @important use this threedots
      renderCell: (params) => {
        return (
          <div
            onClick={(e) => {
              setRowOptionsCords([e["clientX"], e["clientY"]]);
              e.stopPropagation();
              setRowBeingEdited(params.row);
              setEditRowName(params.row.name);
            }}
            className={styles.moreButton}
          >
            {threeDotsIcon()}
          </div>
        );
      },
    },
  ];

  return (
    <div
      className="screen-container "
      style={{
        height: "max-content",
      }}
    >
      <GHeader
        breadcrumbs={false}
        title="Virtual numbers"
        threeDots={false}
        topHeader={{}}
        topHeaderVisible={false}
        bottomHeader={{
          contentTitle: "Virtual numbers",
          buttons: [
            canManageBillingSubscription ? (
              <Button
                buttonText="Add number"
                hierarchy="green"
                disabled={isTrialSubscription(reduxSubscription?.status)}
                onClick={handleAddVN}
              />
            ) : null,
          ],
          isCalendar: false,
        }}
      />
      <GTable
        leftHeader={{
          filters: [
            {
              name: "Location",
              toggle: false,
              isSearch: false,
              payloadFor: "locations",
            },
            {
              name: "Status",
              toggle: false,
              isSearch: false,
              payloadFor: "status",
            },
          ],
          isSearch: true,
        }}
        rightHeader={{ isRefresh: true, isDownload: true }}
        refreshHandler={fetchData}
        rows={rows}
        availableData={availableData}
        dispatchAvailableData={dispatchAvailableData}
        download={{
          data: csvData,
          body: null,
          filename:
            `virtual-numbers-` +
            new Date().toLocaleString("en-US", {
              weekday: "long",
              year: "numeric",
              month: "long",
              day: "numeric",
              hour: "2-digit",
              minute: "2-digit",
              hour12: true,
            }),
        }}
        fromScreen="virtual-nums"
        onSearch={onSearch}
        columns={cols}
        useMuiTable={true}
        isLoading={showLoader}
        getTableRowClassName={() => `teams-table-row`}
        additionalProps={{ bgHover: true }}
        onRowClick={(params, event, details) => {
          if (
            ["In Progress", "Verification Pending"].includes(
              params?.row?.status
            )
          )
            return;
          navigate(`/virtual-numbers/${params.row.number}`, {
            state: {
              id: params.row.cvn_id,
              type: "Calling", //hardcoded to Calling since voice broadcast is not being used
            },
          });
        }}
      />

      {!!rowBeingEdited && (
        <div
          className="row-options-backdrop"
          onClick={() => {
            setRowBeingEdited(null);
          }}
        >
          <div style={{ position: "relative" }}>
            <div
              className="row-options-foreground"
              style={{
                marginLeft: rowOptionsCords[0],
                marginTop: rowOptionsCords[1],
              }}
            >
              <button
                className="t6 nc-gray-900"
                style={{ textAlign: "left" }}
                onClick={(e) => {
                  e.stopPropagation();
                  setShowModal((prev) => {
                    return { ...prev, edit: true };
                  });
                }}
                disabled={!rowBeingEdited?.name}
              >
                Edit
              </button>

              {rowBeingEdited?.country_code !== "+91" && rowBeingEdited?.location?.toLowerCase() !== "india" &&
              <button
                className="t6 nc-gray-900"
                style={{ textAlign: "left" }}
                onClick={(e) => {
                  e.stopPropagation();
                  setShowModal((prev) => {
                    return { ...prev, remove: true };
                  });
                }}
                disabled={!canManageBillingSubscription}
              >
                Remove
              </button>}
            </div>
          </div>
        </div>
      )}

      {showModal?.edit && (
        <GModal
          visible={showModal?.edit}
          closeModal={() => {
            setShowModal((prev) => {
              return { ...prev, edit: false };
            });
            setRowBeingEdited(null);
          }}
          heading="Edit number"
          modalStyle={{ width: "448px", height: "150px" }}
          bottomButtons={[
            <Button
              variant="outlined"
              buttonText="Cancel"
              icon={{}}
              disabled={false}
              isLoading={false}
              hierarchy="white"
              onClick={() => {
                setShowModal((prev) => {
                  return { ...prev, edit: false };
                });
                setRowBeingEdited(null);
              }}
            />,
            <Button
              variant="filled"
              buttonText="Save"
              icon={{}}
              disabled={
                !!rowBeingEdited &&
                (editRowName === rowBeingEdited.name ||
                  editRowName.length === 0)
              }
              isLoading={isLoading}
              hierarchy="green"
              onClick={(e) => updateName(e)}
            />,
          ]}
          body={
            <>
              <form
                className="new-contact-list-form add-contact-form virtual-numbers-form"
                style={{ maxWidth: "400px" }}
              >
                <label
                  className="t7 medium-font"
                  style={{ marginBottom: "32px" }}
                >
                  Number name
                  <input
                    placeholder="Enter name"
                    className="px-14"
                    type="text"
                    onChange={(e) => {
                      setEditRowName(e.target.value);
                    }}
                    value={editRowName}
                  />
                </label>
              </form>
            </>
          }
        />
      )}

      {showModal?.remove && (
        <RemoveVirtualNumberModal
          visible={showModal?.remove}
          onClose={() => {
            setShowModal((prev) => {
              return { ...prev, remove: false };
            });
          }}
          name={rowBeingEdited?.name}
          includedInPlan={rowBeingEdited?.is_included_in_plan}
          onRemove={() =>
            updateAddonHandler(null, "remove", {
              number_ids: [rowBeingEdited?.id],
              quantity: 1,
            })
          }
          isLoading={isLoading}
        />
      )}

      {showModal?.add && (
        <AddVirtualNumberModal
          visible={showModal?.add}
          onClose={() => {
            setShowModal((prev) => {
              return { ...prev, add: false };
            });
          }}
          handleOpenChatWidget={handleOpenChatWidget}
          purchaseHandler={purchaseHandler}
        />
      )}

      {showModal?.payment && (
        <PaymentMethodModal
          visible={showModal?.payment}
          handleClose={() =>
            setShowModal((prev) => {
              return { ...prev, payment: false };
            })
          }
          handlePayWithCard={() => updateAddonHandler(false, "add", vnPayload)}
          handlePayWithCredits={() =>
            updateAddonHandler(true, "add", vnPayload)
          }
        />
      )}

      {showModal?.insufficientCredits && (
        <InsufficientCreditsModal
          visible={showModal?.insufficientCredits}
          handleClose={() =>
            setShowModal((prev) => {
              return { ...prev, insufficientCredits: false };
            })
          }
          handlePayWithCard={() => updateAddonHandler(false, "add", vnPayload)}
          handleAddCredits={() =>
            navigate("/billing", {
              state: { tab: "Credits" },
            })
          }
        />
      )}
    </div>
  );
}

export default VirtualNumber;
